import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks } from '../constants/theme';

const SwitcherContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const SwitcherButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  background: ${Colors.surfaceGlass};
  backdrop-filter: blur(12px);
  border: 1px solid ${Colors.borderLight};
  color: ${Colors.textPrimary};
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.lg}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 300ms cubic-bezier(0.4, 0.0, 0.2, 1);
  min-width: 100px;
  
  &:hover {
    background: ${Colors.surfaceHover};
    border-color: ${Colors.primary};
    transform: translateY(-1px);
    box-shadow: ${Shadows.md};
  }
  
  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 3px ${Colors.primary}20;
  }
`;

const FlagIcon = styled.span`
  font-size: ${FontSizes.md}px;
  line-height: 1;
`;

const LanguageName = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownArrow = styled.span<{ isOpen: boolean }>`
  font-size: ${FontSizes.xs}px;
  transition: transform 200ms ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${Spacing.xs}px);
  right: 0;
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.lg};
  backdrop-filter: blur(20px);
  min-width: 140px;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 200ms cubic-bezier(0.4, 0.0, 0.2, 1);
  z-index: 1000;
`;

const DropdownItem = styled.button<{ isActive: boolean; isRTL?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  width: 100%;
  padding: ${Spacing.md}px;
  background: ${props => props.isActive ? Colors.secondary + '10' : 'transparent'};
  color: ${props => props.isActive ? Colors.secondary : Colors.textPrimary};
  border: none;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  border-radius: ${props => props.isActive ? BorderRadius.md + 'px' : '0'};
  flex-direction: ${props => props.isRTL ? 'row-reverse' : 'row'};
  
  &:first-child {
    border-top-left-radius: ${BorderRadius.lg}px;
    border-top-right-radius: ${BorderRadius.lg}px;
  }
  
  &:last-child {
    border-bottom-left-radius: ${BorderRadius.lg}px;
    border-bottom-right-radius: ${BorderRadius.lg}px;
  }
  
  &:hover {
    background: ${props => props.isActive ? Colors.secondary + '20' : Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }
  
  &:focus {
    outline: none;
    background: ${Colors.surfaceHover};
  }
`;

const LanguageText = styled.span`
  flex: 1;
`;

const CheckIcon = styled.span`
  color: ${Colors.secondary};
  font-size: ${FontSizes.sm}px;
`;

interface Language {
  code: string;
  name: string;
  flag: string;
  nativeName: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English (US)', flag: '🇺🇸', nativeName: 'English' },
  { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪', nativeName: 'Svenska' },
  { code: 'da', name: 'Danish', flag: '🇩🇰', nativeName: 'Dansk' },
  { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦', nativeName: 'العربية' }
];

const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const currentLanguage = languages.find(lang => lang.code === i18n.language) 
    || languages.find(lang => lang.code.split('-')[0] === i18n.language.split('-')[0])
    || languages[0];
  
  const handleLanguageChange = async (langCode: string) => {
    try {
      await i18n.changeLanguage(langCode);
      setIsOpen(false);
      
      // Update URL parameter to persist language across page reloads and shared links
      const url = new URL(window.location.href);
      url.searchParams.set('lang', langCode);
      window.history.replaceState({}, '', url.toString());
      
      // Store preference in localStorage (backup for when URL param is missing)
      localStorage.setItem('i18nextLng', langCode);
      
      // RTL handling is now done in our rtl.ts utility via the i18n event listener
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-language-switcher]')) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);
  
  return (
    <SwitcherContainer data-language-switcher>
      <SwitcherButton 
        onClick={toggleDropdown}
        aria-label={t('language.switchTo', { language: currentLanguage.nativeName })}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <FlagIcon>{currentLanguage.flag}</FlagIcon>
        <LanguageName>{currentLanguage.code.toUpperCase()}</LanguageName>
        <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
      </SwitcherButton>
      
      <DropdownMenu isOpen={isOpen} role="listbox">
        {languages.map((language) => {
          const isRTL = language.code === 'ar';
          const isActive = language.code === i18n.language || 
                          (language.code.split('-')[0] === i18n.language.split('-')[0]);
          
          return (
            <DropdownItem
              key={language.code}
              isActive={isActive}
              isRTL={isRTL}
              onClick={() => handleLanguageChange(language.code)}
              role="option"
              aria-selected={isActive}
            >
              <FlagIcon>{language.flag}</FlagIcon>
              <LanguageText dir={isRTL ? 'rtl' : 'ltr'}>
                {language.nativeName}
              </LanguageText>
              {isActive && (
                <CheckIcon>✓</CheckIcon>
              )}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </SwitcherContainer>
  );
};

export default LanguageSwitcher;